import { UseCases } from "@aptus/frontend-core";
import { Events } from "event";
import { useEffect, useState } from "react";
import { RedirectSuggestion, resolveRedirect } from "utils/locales";

// How long the modal stays hidden after the visitor chooses to stay on this site.
const SNOOZE_MS = 5 * 60 * 1000;
const DISMISSED_AT_KEY = "locationModalDismissedAt";
// Replaced by the snooze above. Cleared on every run so visitors who once chose
// "never ask again" aren't silenced forever by a key nothing reads anymore.
const LEGACY_DENIED_KEY = "deniedShowLocationModal";

interface Result {
	shouldShowLocationRedirectModal: boolean;
	redirectSuggestion: RedirectSuggestion | null;

	tryShowLocationModal: Events["showLocationRedirectModal"];
	changeToMostRelevantSiteForLanguage: Events["changeToMostRelevantSiteForLanguage"];
	stayOnSite: Events["stayOnSite"];
}

const readDismissedAt: () => number | null = () => {
	const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY));
	return dismissedAt > 0 ? dismissedAt : null;
};

export const useLocationRedirectUseCases: UseCases<undefined, Result> = () => {
	const [redirectSuggestion, setRedirectSuggestion] = useState<Result["redirectSuggestion"]>(null);
	// Drives the re-show timer. Seeded from storage so a reload mid-snooze keeps counting down.
	const [dismissedAt, setDismissedAt] = useState<number | null>(readDismissedAt);

	const isSnoozed: () => boolean = () => {
		const previouslyDismissedAt = readDismissedAt();

		if (previouslyDismissedAt === null) {
			return false;
		}

		if (Date.now() - previouslyDismissedAt < SNOOZE_MS) {
			return true;
		}

		localStorage.removeItem(DISMISSED_AT_KEY);
		return false;
	};

	const tryShowLocationModal: Result["tryShowLocationModal"] = () => {
		localStorage.removeItem(LEGACY_DENIED_KEY);

		if (isSnoozed()) {
			setRedirectSuggestion(null);
			return;
		}

		setRedirectSuggestion(resolveRedirect({
			host: window.location.host,
			languages: navigator.languages || [navigator.language],
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		}));
	};

	const changeToMostRelevantSiteForLanguage: Result["changeToMostRelevantSiteForLanguage"] = () => {
		if (!redirectSuggestion) {
			return;
		}

		setRedirectSuggestion(null);
		window.location.replace(`https://${redirectSuggestion.targetHost}${window.location.pathname}`);
	};

	const stayOnSite: Result["stayOnSite"] = () => {
		const now = Date.now();

		setRedirectSuggestion(null);
		localStorage.setItem(DISMISSED_AT_KEY, String(now));
		setDismissedAt(now);
	};

	useEffect(() => {
		tryShowLocationModal();
	}, []);

	// Ask again once the snooze runs out, without needing a page reload.
	useEffect(() => {
		if (dismissedAt === null) {
			return undefined;
		}

		const remainingMs = (dismissedAt + SNOOZE_MS) - Date.now();

		if (remainingMs <= 0) {
			return undefined;
		}

		const timeout = window.setTimeout(() => {
			setDismissedAt(null);
			tryShowLocationModal();
		}, remainingMs);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [dismissedAt]);

	return {
		shouldShowLocationRedirectModal: redirectSuggestion !== null,
		redirectSuggestion,
		tryShowLocationModal,
		changeToMostRelevantSiteForLanguage,
		stayOnSite,
	};
};
